import { useCallback } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { ContentListPage } from '../components/ContentListPage';
import { PopularArticlesSidebar } from '../components/article/PopularArticlesSidebar';
import { RecentIntegrationsSidebar } from '../components/integration/RecentIntegrationsSidebar';
import { ShareSidebar } from '../components/article/ShareSidebar';
import { ArticleReadMore } from '../components/article/ArticleReadMore';
import { ArticleContent } from '../components/article/ArticleBlockRenderer';
import { ArticleHeroMedia } from '../components/article/ArticleHeroMedia';
import { useCachedPublicGet } from '../hooks/useCachedPublicGet';
import { useCachedPublishedArticles } from '../hooks/useCachedPublishedArticles';
import type { ArticleRow } from '../../../shared/schemas/article';

function buildRelatedLists(
  article: ArticleRow | null,
  articleList: ArticleRow[] | null,
  integrationList: ArticleRow[] | null,
  slug: string | undefined,
): { popular: ArticleRow[]; readMore: ArticleRow[] } {
  if (!article || !articleList || !integrationList || !slug) {
    return { popular: [], readMore: [] };
  }

  const relatedArticles =
    article.type === 'integration' ? integrationList : articleList;
  const filtered = relatedArticles.filter((entry) => entry.slug !== slug);

  return {
    popular: filtered.slice(0, 5),
    readMore: filtered.slice(0, 6),
  };
}

export function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const articlePath = slug ? `/api/articles/${slug}` : undefined;
  const {
    data: article,
    loading: articleLoading,
    error: articleError,
  } = useCachedPublicGet<ArticleRow>(articlePath);
  const { data: articleList } = useCachedPublishedArticles(
    '/api/articles?type=article&limit=30',
  );
  const { data: integrationList } = useCachedPublishedArticles(
    '/api/articles?type=integration&limit=30',
  );

  const isReady = Boolean(article) && !articleLoading;
  const sidebarReady = Boolean(article) && articleList !== null && integrationList !== null;
  const contentReady = isReady && sidebarReady;
  const showError = !articleLoading && Boolean(articleError || (slug && !article));
  const { popular, readMore } = buildRelatedLists(article, articleList, integrationList, slug);
  const articleUrl = `${window.location.origin}/articles/${slug ?? ''}`;

  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(articleUrl).then(
      () => { /* success */ },
      (err: unknown) => {
        console.warn('[ArticlePage:copyLink] clipboard write failed', { err, articleUrl });
      },
    );
  }, [articleUrl]);

  if (isReady && article && article.type === 'feature') {
    return <Navigate to={`/features/${slug}`} replace />;
  }

  const leftPanel = sidebarReady ? (
    article!.type === 'integration' ? (
      <RecentIntegrationsSidebar entries={popular} currentSlug={slug ?? ''} />
    ) : (
      <PopularArticlesSidebar
        articles={popular.map((entry) => ({ title: entry.title, slug: entry.slug }))}
      />
    )
  ) : null;

  if (showError) {
    return (
      <ContentListPage title="Article not found" leftPanel={leftPanel}>
        <p className="text-body text-text-tertiary">
          {articleError ?? 'Article not found'}
        </p>
        <a href="/articles" className="text-caption link-default">
          Browse all articles
        </a>
      </ContentListPage>
    );
  }

  const subtitle = article?.published_at ? (
    <p className="text-btn text-text-tertiary">
      {new Date(article.published_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })}
      {article.author ? ` by ${article.author}` : ''}
    </p>
  ) : undefined;

  return (
    <ContentListPage
      title={article?.title ?? ''}
      subtitle={article ? subtitle : undefined}
      contentReady={contentReady}
      leftPanel={leftPanel}
      rightPanel={<ShareSidebar articleUrl={articleUrl} />}
      footer={
        contentReady && readMore.length > 0 ? (
          <ArticleReadMore
            articles={readMore.map((entry) => ({
              title: entry.title,
              description: entry.excerpt ?? '',
              slug: entry.slug,
            }))}
          />
        ) : undefined
      }
    >
      {article && (
        <>
          <ArticleHeroMedia
            heroMedia={article.hero_media ?? undefined}
            legacyMiniDemoId={article.mini_demo_id}
          />

          <ArticleContent content={article.content} />

          <button
            type="button"
            onClick={handleCopyLink}
            className="site-btn-secondary site-btn-md"
          >
            Copy article link
            <ArrowUpRight size={14} />
          </button>

          {subtitle}
        </>
      )}
    </ContentListPage>
  );
}
